import { Box, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { FEATURE_FLAGS } from '../../constants';
import { useUser } from '@hooks/useUser';
import { isFeatureFlagEnabled } from '../../utils/featureFlagUtils';
import CheckpointActivityLog from './CheckpointActivityLog';
import QuickScanActivityLog, { QUICK_SCAN_TAB } from './QuickScanActivityLog';

const CHECKPOINT_TAB = 'checkpoints';

const ClearanceLogsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  // The Quick Scan tab is only for agencies with the WEB_QUICK_SCAN flag. Without
  // it, a ?tab=quick_scans link opens the Checkpoints tab.
  const isQuickScanEnabled = isFeatureFlagEnabled(user?.profile, FEATURE_FLAGS.WEB_QUICK_SCAN);
  const tab = isQuickScanEnabled && searchParams.get('tab') === QUICK_SCAN_TAB ? QUICK_SCAN_TAB : CHECKPOINT_TAB;

  // TODO: eventually we should standardize our breakpoints and 
  // avoid passing in specific pixel dimensions like this:
  const isSmallScreen = useMediaQuery('(max-width:1024px)');

  // Each tab starts on its first page with no search or filters. The date
  // range carries over.
  const changeTab = (nextTab: string) => {
    const params: Record<string, string> = {};
    if (nextTab === QUICK_SCAN_TAB) params.tab = QUICK_SCAN_TAB;
    const start = searchParams.get('start');
    const end = searchParams.get('end');
    if (start) params.start = start;
    if (end) params.end = end;
    setSearchParams(params, { replace: true });
  };

  return (
    <Box
      sx={{
        height: isSmallScreen ? 'calc(100vh - 80px)' : 'calc(100vh - 48px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Typography variant='xxl'>Activity</Typography>
      <Tabs
        value={tab}
        onChange={(_, nextTab: string) => changeTab(nextTab)}
        aria-label='Activity logs'
        sx={{ mt: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label='Checkpoints' value={CHECKPOINT_TAB} id='activity-tab-checkpoints' aria-controls='activity-panel' />
        {isQuickScanEnabled && (
          <Tab label='Quick Scan' value={QUICK_SCAN_TAB} id='activity-tab-quick_scans' aria-controls='activity-panel' />
        )}
      </Tabs>
      <Box
        role='tabpanel'
        id='activity-panel'
        aria-labelledby={`activity-tab-${tab}`}
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {tab === QUICK_SCAN_TAB ? <QuickScanActivityLog /> : <CheckpointActivityLog />}
      </Box>
    </Box>
  );
};

export default ClearanceLogsPage;
